import { ErrorHandler } from '@src/components/ErrorHandler';
import { ListTerritoryProvinces } from '@src/includes/functions';
import { fieldValidator } from '@src/includes/validation';
import { ToolTipStyles } from '@src/routes/private/MemberProfile/components/Modals/includes/styles';
import { Box } from '@uhg-abyss/web/ui/Box';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';

import fields from '../../../../includes/fields.json';

/**
 * Tab: UsAddress
 *
 * Displays information about a member's policy, relevant to the United States postal address.
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
export function UsAddress(props) {
  const { form, isInternational, voidFormDisabled } = props;

  const city = form.watch('ui[usAddress][city]');
  const countryCode = form.watch('ui[usAddress][countryCode]');
  const postalCode = form.watch('ui[usAddress][postalCode]');
  const stateOrRegion = form.watch('ui[usAddress][stateOrRegion]');
  const streetAddress = form.watch('ui[usAddress][streetAddress]');
  const suite = form.watch('ui[usAddress][suite]');

  /**
   * states
   *
   * Retrieves a list of states.
   *
   * @type {{code: *, name: *}[]}
   */
  const states = useMemo(() => {
    return ListTerritoryProvinces(countryCode);
  }, [countryCode]);

  useEffect(() => {
    if (!isEmpty(streetAddress)) {
      form.validate(
        'ui[usAddress][streetAddress]',
        () => {},
        () => {}
      );
    }

    if (!isEmpty(suite)) {
      form.validate(
        'ui[usAddress][suite]',
        () => {},
        () => {}
      );
    }

    if (!isEmpty(postalCode)) {
      form.validate(
        'ui[usAddress][postalCode]',
        () => {},
        () => {}
      );
    }
  }, [streetAddress, suite, postalCode]);

  return (
    <ErrorHandler location="src/routes/private/MemberProfile/components/Modals/CondfComm/components/Address/components/UsAddress/UsAddress.jsx">
      <Grid className="section first">
        <Box className="box">
          <Heading offset={5}>Address Line 1:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.streetAddress}
            isDisabled={isInternational || voidFormDisabled}
            validators={{
              ...fields?.usAddress?.streetAddress?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.usAddress?.streetAddress, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 2:</Heading>
        </Box>
        <Grid.Col span={{ lg: 4, xl: 4, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.suite}
            isDisabled={isInternational || voidFormDisabled}
            validators={{
              ...fields?.usAddress?.suite?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.usAddress?.suite, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
      <Grid className="section">
        <Box className="box">
          <Heading offset={5}>Address Line 3:</Heading>
        </Box>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <SelectInput
            {...fields?.usAddress?.stateOrRegion}
            isDisabled={isInternational || voidFormDisabled}
            options={states.map((state) => {
              return {
                label: state?.name,
                value: state?.code,
              };
            })}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.city}
            isDisabled={isInternational || voidFormDisabled || isEmpty(stateOrRegion)}
          />
        </Grid.Col>
        <Grid.Col span={{ lg: 3, xl: 3, xs: 12 }}>
          <TextInput
            {...fields?.usAddress?.postalCode}
            formatter={(value) => {
              return value.replace(/[-`&/\\#,+()$~%.'":*?<>{}@!^_=;\][|]/g, '');
            }}
            isDisabled={isInternational || voidFormDisabled || isEmpty(stateOrRegion) || isEmpty(city)}
            label={
              <ToolTipStyles>
                <Tooltip asChild content="will allow zip + 4 or international zip code">
                  <strong>{fields?.usAddress?.postalCode?.label}</strong>
                </Tooltip>
              </ToolTipStyles>
            }
            validators={{
              ...fields?.usAddress?.postalCode?.validators,
              validate: {
                customValidator: (value) => {
                  return fieldValidator(fields?.usAddress?.postalCode, value);
                },
              },
            }}
          />
        </Grid.Col>
      </Grid>
    </ErrorHandler>
  );
}

UsAddress.propTypes = {
  form: PropTypes.shape({
    getValues: PropTypes.func,
  }),
  voidFormDisabled: PropTypes.bool,
};

UsAddress.defaultProps = {
  form: {
    getValues: () => {},
  },
  voidFormDisabled: false,
};
